import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faDiagramNested,
  faMagnifyingGlass,
  faTrash,
} from "@fortawesome/pro-regular-svg-icons";
import React from "react";
import { twJoin } from "tailwind-merge";

import { Icon } from "src/base-components/Icon";

export type ToolType =
  | "flow"
  | "analyze_document"
  | "sleep"
  | "random_number"
  | "echo";

type ToolCardProps = {
  name: string;
  description?: string;
  type: ToolType;
  onClick?: () => void;
  onDelete?: () => void;
  selected?: boolean;
  dataLoc?: string;
};

type ToolIconConfig = {
  icon: IconDefinition;
  bgColor: string;
  iconColor: string;
};

const TOOL_ICON_CONFIG: Record<ToolType, ToolIconConfig> = {
  flow: {
    icon: faDiagramNested,
    bgColor: "bg-gray-50",
    iconColor: "text-gray-600",
  },
  // These will be removed soon, just to make
  // typescript happy
  analyze_document: {
    icon: faMagnifyingGlass,
    bgColor: "bg-gray-100",
    iconColor: "text-gray-500",
  },
  sleep: {
    icon: faMagnifyingGlass,
    bgColor: "bg-gray-100",
    iconColor: "text-gray-500",
  },
  random_number: {
    icon: faMagnifyingGlass,
    bgColor: "bg-gray-100",
    iconColor: "text-gray-500",
  },
  echo: {
    icon: faMagnifyingGlass,
    bgColor: "bg-gray-100",
    iconColor: "text-gray-500",
  },
};

export const ToolCard: React.FC<ToolCardProps> = ({
  name,
  description,
  type,
  onClick,
  onDelete,
  selected,
  dataLoc,
}) => {
  const iconConfig = TOOL_ICON_CONFIG[type];

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  return (
    <div
      className={twJoin(
        "group flex items-center gap-3 rounded-lg border bg-white p-2",
        selected ? "border-indigo-500" : "border-gray-200",
        onClick && !selected && "cursor-pointer hover:border-gray-300",
      )}
      data-loc={dataLoc}
      onClick={onClick}
    >
      <div
        className={twJoin(
          "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg",
          iconConfig.bgColor,
        )}
      >
        <Icon color={iconConfig.iconColor} icon={iconConfig.icon} size="sm" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-gray-800 font-inter-semibold-13px">
          {name}
        </p>
        {description && (
          <p className="truncate text-gray-500 font-inter-normal-12px">
            {description}
          </p>
        )}
      </div>
      {onDelete && (
        <button
          className="flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
          data-loc={`${dataLoc}-delete`}
          type="button"
          onClick={handleDelete}
        >
          <Icon
            color="text-gray-400 hover:text-red-500"
            icon={faTrash}
            size="sm"
          />
        </button>
      )}
    </div>
  );
};
